/**
 * Report-native player-page sections rendered from the roll-up blob's
 * flattened scope rows (`/api/player_stats/player/<id>` — values `v`,
 * percentiles `p`, keyed by the served registry's column keys) plus the
 * payload's `bio`/`box` blocks.
 *
 * Visual grammar = the personnel report, ported faithfully: numbered section
 * heads with right-aligned context notes, full-width tables with group
 * borders, the grey group-band row on the snapshot, KPI bands, greyed
 * sub-floor rows, chips/needles from the shared components.
 */
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import PercentileChip from "../PercentileChip";
import GreyNumeral from "../GreyNumeral";
import {
  CONDENSED_FONT_FAMILY,
  DISPLAY_FONT_FAMILY,
  REPORT_RED,
} from "../constants";
import { nth } from "../utils";
import { formatSigned } from "../../util/formatSigned";
import type {
  PlayerStatColumn,
  PlayerStatSection,
  PlayerStatsScopeRow,
} from "../../models/PlayerStats";

// ---------- data access + formatting ----------

export const val = (row: PlayerStatsScopeRow | undefined, key: string) =>
  (row?.v as Record<string, number> | undefined)?.[key];
// A tendency's percentile as the grey number after its value (it replaced the freq needle, Trevor 2026-10-10)
const pctNum = (p?: number) => (p === undefined || p === null ? undefined : Math.round(p));

export const pct = (row: PlayerStatsScopeRow | undefined, key: string) =>
  (row?.p as Record<string, number> | undefined)?.[key];

/** Action count (N): the registry serves it as `<side>_<action>_<bd>_o_n` on
 * every drill-down of the action — any one of them carries the same value. */
export function actionCount(
  row: PlayerStatsScopeRow | undefined,
  side: "off" | "def",
  action: string,
): number | undefined {
  const values = row?.v as Record<string, number> | undefined;
  if (!values) return undefined;
  const re = new RegExp(`^${side}_${action}_[a-z_]+_o_n$`);
  const key = Object.keys(values).find((k) => re.test(k));
  return key ? values[key] : undefined;
}

export function fmt(value: number | undefined, format?: string): string {
  if (value === undefined || value === null || isNaN(Number(value))) return "—";
  switch (format) {
    case "n0":
      return Math.round(value).toLocaleString();
    case "ppp":
    case "ratio":
      return value.toFixed(2);
    case "pm":
      return formatSigned(value, 1);
    case "pm2":
      return formatSigned(value, 2);
    case "frac-pct": // stored 0-1, displayed 31.8
      return (value * 100).toFixed(1);
    case "frac-pm": // stored 0-1 diff, displayed +1.5 (points of pct)
      return formatSigned(value * 100, 1);
    default:
      return value.toFixed(1);
  }
}

export function heightLabel(heightIn?: number | null): string | null {
  if (!heightIn) return null;
  return `${Math.floor(heightIn / 12)}'${Math.round(heightIn % 12)}"`;
}

// Greyed sub-floor rows: the personnel display floor.
export const MIN_CHANCES = 50;

// ---------- shared primitives ----------

/** BASELINE / SCOUTING FOCUS divider (personnel .grp kicker). */
export function Kicker({ label }: { label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mt: 3, mb: 1 }}>
      <Typography
        component="span"
        sx={{
          fontFamily: DISPLAY_FONT_FAMILY,
          fontWeight: 800,
          fontSize: "0.92rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: REPORT_RED,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: "2px", backgroundColor: "#000" }} />
    </Stack>
  );
}

/** Numbered section header + right-aligned context note (personnel .sh). */
export function SectionHead({
  code,
  title,
  note,
}: {
  code?: string;
  title: string;
  note?: string;
}) {
  // The note drops below the title when both don't fit (a two-up pair at the paper's width: the roster preview
  // prints unshrunk) rather than squeezing the title into one word per line.
  return (
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={0.75}
      useFlexGap
      flexWrap="wrap"
      sx={{ mb: 0.5, mt: 2, rowGap: 0 }}
    >
      {code && (
        <Typography
          component="span"
          sx={{
            fontFamily: CONDENSED_FONT_FAMILY,
            fontSize: "0.782rem",
            fontWeight: 700,
            color: "text.disabled",
            letterSpacing: "0.08em",
          }}
        >
          {code}
        </Typography>
      )}
      <Typography
        sx={{
          fontFamily: DISPLAY_FONT_FAMILY,
          fontWeight: 700,
          fontSize: "1.15rem",
          textTransform: "uppercase",
          letterSpacing: "0.02em",
        }}
      >
        {title}
      </Typography>
      <Stack sx={{ flex: 1 }} />
      {note && (
        <Typography
          sx={{
            fontFamily: CONDENSED_FONT_FAMILY,
            fontSize: "0.782rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            color: "text.secondary",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {note}
        </Typography>
      )}
    </Stack>
  );
}

/** Chip with the percentile numeral behind a hover tooltip (kit tier), or
 * shown small on the chip (focus tier — `numeral`). */
export function HoverChip({
  value,
  percentile,
  numeral = false,
  plain = false,
}: {
  value: string;
  percentile?: number;
  numeral?: boolean;
  /** Render as a plain value (greyed rows strip their chips). */
  plain?: boolean;
}) {
  if (plain || percentile === undefined || percentile === null) {
    return (
      <Typography component="span" sx={{ fontSize: "0.862rem", fontWeight: 700 }}>
        {value}
      </Typography>
    );
  }
  const chip = (
    <PercentileChip
      value={value}
      percentile={percentile}
      numeral={numeral ? Math.round(percentile) : undefined}
    />
  );
  return numeral ? (
    chip
  ) : (
    <Tooltip title={`${nth(Math.round(percentile))} percentile`} enterDelay={300} placement="top" arrow>
      {chip}
    </Tooltip>
  );
}

// personnel zebra (--pr-zebra): every even body row across all page tables.
export const ZEBRA_SX = {
  "tbody tr:nth-of-type(even) td": { backgroundColor: "#f7f6f4" },
};

// Personnel table.t grammar, scaled from print px: tight 2px/5px padding,
// visible hairline row borders, semibold values, heavy header rule.
export const CELL_SX = {
  px: 0.6,
  py: 0.25,
  fontFamily: CONDENSED_FONT_FAMILY,
  fontSize: "0.862rem",
  fontWeight: 600,
  lineHeight: 1.3,
  textAlign: "center" as const,
  borderBottom: "1px solid #e6e4e1",
  whiteSpace: "nowrap" as const,
};
export const HEAD_SX = {
  ...CELL_SX,
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  fontSize: "0.782rem",
  color: "#4c4c4c",
  borderBottom: "3px solid #4c4c4c",
};
export const ROW_LABEL_SX = { ...CELL_SX, textAlign: "left" as const, fontWeight: 700 };
const GROUP_BORDER = { borderLeft: "1px solid #cfccc8" };
const COUNT_SX = { ...CELL_SX, color: "text.secondary", fontWeight: 400, fontSize: "0.805rem" };

// ---------- header band ----------

export function HeaderBand({
  name,
  headshot,
  pos,
  heightIn,
  weightLb,
  ageYrs,
  seasonScope,
  seasonLabel,
}: {
  name: string;
  headshot?: string | null;
  pos?: string | null;
  heightIn?: number | null;
  weightLb?: number | null;
  ageYrs?: number | null;
  seasonScope?: PlayerStatsScopeRow;
  seasonLabel?: string;
}) {
  const bioBits = [
    heightLabel(heightIn),
    weightLb ? `${Math.round(weightLb)} LB` : null,
    ageYrs ? `${ageYrs.toFixed(1)} YRS` : null,
  ].filter(Boolean);
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0.5 }}>
      {headshot && (
        // Personnel .shot frame (grey ground, hairline border, cover crop from
        // the top) at ~1.6× the print size (user call 2026-08-25).
        <Box
          component="img"
          src={headshot}
          alt=""
          sx={{
            width: 155,
            height: 120,
            objectFit: "cover",
            objectPosition: "center top",
            backgroundColor: "#efedeb",
            border: "1px solid #ddd",
            flexShrink: 0,
          }}
        />
      )}
      <Stack spacing={0.3}>
        <Typography
          sx={{
            fontFamily: DISPLAY_FONT_FAMILY,
            fontWeight: 900,
            fontSize: "2.3rem",
            lineHeight: 1,
            textTransform: "uppercase",
          }}
        >
          {name}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ fontFamily: CONDENSED_FONT_FAMILY }}>
          {pos && (
            <Typography sx={{ fontFamily: "inherit", fontSize: "0.92rem", fontWeight: 700 }}>{pos}</Typography>
          )}
          {bioBits.length > 0 && (
            <>
              <Typography sx={{ fontFamily: "inherit", fontSize: "0.92rem", color: "#ccc" }}>|</Typography>
              <Typography sx={{ fontFamily: "inherit", fontSize: "0.92rem", color: "text.secondary" }}>
                {bioBits.join(" · ")}
              </Typography>
            </>
          )}
          {seasonScope && (
            <>
              <Typography sx={{ fontFamily: "inherit", fontSize: "0.92rem", color: "#ccc" }}>|</Typography>
              <Typography sx={{ fontFamily: "inherit", fontSize: "0.92rem", color: "text.secondary" }}>
                {seasonLabel && `${seasonLabel}: `}
                <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {seasonScope.team ?? ""}
                </Box>
                {seasonScope.gp != null && ` ${seasonScope.gp} GP`}
                {seasonScope.league && seasonScope.league !== "NBA" && ` · ${seasonScope.league}`}
              </Typography>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}

// ---------- 01: box score per game ----------

export interface BoxScoreRow {
  season: string;
  tm: string | null;
  gp: number | null;
  gs: number | null;
  mpg: number | null;
  pts: number | null;
  reb: number | null;
  ast: number | null;
  stl: number | null;
  blk: number | null;
  tov: number | null;
  fg_pct: number | null;
  p3_pct: number | null;
  ft_pct: number | null;
}

export function BoxScoreTable({ code, box }: { code?: string; box: BoxScoreRow[] }) {
  if (!box?.length) return null;
  const one = (v: number | null) => (v === null || v === undefined ? "—" : v.toFixed(1));
  return (
    <Box>
      <SectionHead code={code} title="Box Score — Per Game" note={`Last ${box.length} seasons`} />
      <Table size="small" sx={{ width: "100%", ...ZEBRA_SX }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_SX, textAlign: "left" }}>Season</TableCell>
            <TableCell sx={HEAD_SX}>TM</TableCell>
            <TableCell sx={HEAD_SX}>GP</TableCell>
            <TableCell sx={HEAD_SX}>GS</TableCell>
            <TableCell sx={HEAD_SX}>MPG</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>PTS</TableCell>
            <TableCell sx={HEAD_SX}>REB</TableCell>
            <TableCell sx={HEAD_SX}>AST</TableCell>
            <TableCell sx={HEAD_SX}>STL</TableCell>
            <TableCell sx={HEAD_SX}>BLK</TableCell>
            <TableCell sx={HEAD_SX}>TOV</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>FG%</TableCell>
            <TableCell sx={HEAD_SX}>3P%</TableCell>
            <TableCell sx={HEAD_SX}>FT%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {box.map((r) => (
            <TableRow key={r.season}>
              <TableCell sx={ROW_LABEL_SX}>{r.season}</TableCell>
              <TableCell sx={COUNT_SX}>{r.tm ?? "—"}</TableCell>
              <TableCell sx={COUNT_SX}>{r.gp ?? "—"}</TableCell>
              <TableCell sx={COUNT_SX}>{r.gs ?? "—"}</TableCell>
              <TableCell sx={CELL_SX}>{one(r.mpg)}</TableCell>
              <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }}>{one(r.pts)}</TableCell>
              <TableCell sx={CELL_SX}>{one(r.reb)}</TableCell>
              <TableCell sx={CELL_SX}>{one(r.ast)}</TableCell>
              <TableCell sx={CELL_SX}>{one(r.stl)}</TableCell>
              <TableCell sx={CELL_SX}>{one(r.blk)}</TableCell>
              <TableCell sx={CELL_SX}>{one(r.tov)}</TableCell>
              <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }}>{one(r.fg_pct)}</TableCell>
              <TableCell sx={CELL_SX}>{one(r.p3_pct)}</TableCell>
              <TableCell sx={CELL_SX}>{one(r.ft_pct)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------- 02: advanced snapshot (full-width table with group bands) ----------

export function AdvancedSnapshot({
  code,
  scope,
  section,
}: {
  code?: string;
  scope: PlayerStatsScopeRow;
  section?: PlayerStatSection;
}) {
  if (!section) return null;
  const cols = section.columns.filter((c) => val(scope, c.key) !== undefined);
  if (!cols.length) return null;
  const groups: [string, PlayerStatColumn[]][] = [];
  for (const c of cols) {
    const g = groups.find(([name]) => name === c.group);
    if (g) g[1].push(c);
    else groups.push([c.group, [c]]);
  }
  return (
    <Box>
      <SectionHead code={code} title="Advanced Snapshot" />
      <Table size="small" sx={{ width: "100%", ...ZEBRA_SX }}>
        <TableHead>
          <TableRow>
            {groups.map(([group, gcols], gi) => (
              <TableCell
                key={group}
                colSpan={gcols.length}
                sx={{
                  ...CELL_SX,
                  textAlign: "left",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontSize: "0.747rem",
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #ddd",
                  ...(gi > 0 ? GROUP_BORDER : {}),
                }}
              >
                {group}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            {groups.flatMap(([group, gcols], gi) =>
              gcols.map((c, ci) => (
                <TableCell key={c.key} sx={{ ...HEAD_SX, ...(gi > 0 && ci === 0 ? GROUP_BORDER : {}) }}>
                  {c.label}
                </TableCell>
              )),
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {groups.flatMap(([group, gcols], gi) =>
              gcols.map((c, ci) => (
                <TableCell key={c.key} sx={{ ...CELL_SX, ...(gi > 0 && ci === 0 ? GROUP_BORDER : {}) }}>
                  <HoverChip value={fmt(val(scope, c.key), c.format)} percentile={pct(scope, c.key)} />
                </TableCell>
              )),
            )}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------- role & efficiency / efficacy tables ----------

// The page's per-100 base is his own floor time; the team report's player tables count per 100 team possessions in
// his games, so the two rates differ by design and the header can't say which on its own.
const PER100_TIP: Record<"off" | "def", string> = {
  off: "Actions per 100 of his on-court halfcourt possessions",
  def: "Actions he defended per 100 of his on-court halfcourt defensive possessions",
};

// NBA blobs split the ball-screen roles PR / Sm-Sm by the screen defender
// (TR3); SkillCorner blobs keep the all-screens keys. A scope carries one set,
// so the rows it lacks drop out below.
const ACTIONS: [string, string][] = [
  ["pr_bhr", "PR Ballhandler"],
  ["smsm_bhr", "Sm-Sm Ballhandler"],
  ["pick_bhr", "P&R Ballhandler"],
  ["isolation", "Isolation"],
  ["handoff", "Handoff"],
  ["post", "Post Up"],
  ["drive", "Drive"],
  ["obs", "Off-Ball Screen"],
  ["pr_screener", "PR Screener"],
  ["smsm_screener", "Sm-Sm Screener"],
  ["pnr_screener", "P&R Screener"],
];

export function ActionProfile({
  code,
  scope,
  side,
}: {
  code?: string;
  scope: PlayerStatsScopeRow;
  side: "off" | "def";
}) {
  const fp = side === "off" ? "f_" : "df_";
  const ep = side === "off" ? "e_" : "de_";
  // Finish/pass split rides the generated outcome-breakdown columns. Drives
  // carry no ActionOutcome, so their cells read as em dashes (personnel needed
  // a dedicated pull for the drive split too).
  const op = (key: string, leaf: string) => `${side}_${key}_outcome_${leaf}`;
  const rows = ACTIONS.map(([key, label]) => ({
    key,
    label,
    per100: val(scope, fp + key),
    per100P: pct(scope, fp + key),
    n: actionCount(scope, side, key),
    ppp: val(scope, ep + key),
    pppP: pct(scope, ep + key),
    xppp: val(scope, ep + key + "_x"),
    xpppP: pct(scope, ep + key + "_x"),
    finShare: val(scope, op(key, "s_finish")),
    finShareP: pct(scope, op(key, "s_finish")),
    finPpp: val(scope, op(key, "e_finish")),
    passPpp: val(scope, op(key, "e_pass")),
  }))
    .filter((r) => r.per100 !== undefined || r.ppp !== undefined)
    .sort((a, b) => (b.per100 ?? 0) - (a.per100 ?? 0));
  if (!rows.length) return null;
  const showX = rows.some((r) => r.xppp !== undefined);
  const showN = rows.some((r) => r.n !== undefined);
  const showFin = rows.some((r) => r.finShare !== undefined);
  const anyGrey = rows.some((r) => (r.n ?? Infinity) < MIN_CHANCES);
  // The cutter's Finish / Pass split isn't served (his shots off screens have their own table), so his
  // cells stay empty rather than reading as missing data. The defensive split is unchanged.
  const noFinish = (key: string) => side === "off" && key === "obs";
  return (
    <Box>
      <SectionHead
        code={code}
        title={side === "off" ? "Offensive Role & Efficiency" : "Defensive Role & Efficacy"}
        note={side === "off" ? "as primary player" : "allowed as primary defender"}
      />
      <Table size="small" sx={{ width: "100%", ...ZEBRA_SX }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_SX, textAlign: "left" }}>Action</TableCell>
            <Tooltip title={PER100_TIP[side]} enterDelay={300} placement="top" arrow>
              <TableCell sx={HEAD_SX}>/100</TableCell>
            </Tooltip>
            {showN && <TableCell sx={HEAD_SX}>N</TableCell>}
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>{side === "off" ? "PPP" : "PPP Allowed"}</TableCell>
            {showX && <TableCell sx={HEAD_SX}>xPPP</TableCell>}
            {showFin && (
              <>
                <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>Finish%</TableCell>
                <TableCell sx={HEAD_SX}>PPP Fin</TableCell>
                <TableCell sx={HEAD_SX}>PPP Pass</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const grey = (r.n ?? Infinity) < MIN_CHANCES;
            return (
              <TableRow key={r.key} sx={grey ? { opacity: 0.45 } : undefined}>
                <TableCell sx={ROW_LABEL_SX}>{r.label}</TableCell>
                <TableCell sx={CELL_SX}>
                  {fmt(r.per100, "r1")}
                  <GreyNumeral>{pctNum(r.per100P)}</GreyNumeral>
                </TableCell>
                {showN && <TableCell sx={COUNT_SX}>{r.n !== undefined ? Math.round(r.n).toLocaleString() : "—"}</TableCell>}
                <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }}>
                  <HoverChip value={fmt(r.ppp, "ppp")} percentile={r.pppP} numeral plain={grey} />
                </TableCell>
                {showX && (
                  <TableCell sx={CELL_SX}>
                    <HoverChip value={fmt(r.xppp, "ppp")} percentile={r.xpppP} numeral plain={grey} />
                  </TableCell>
                )}
                {showFin && noFinish(r.key) && (
                  <>
                    <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }} />
                    <TableCell sx={CELL_SX} />
                    <TableCell sx={CELL_SX} />
                  </>
                )}
                {showFin && !noFinish(r.key) && (
                  <>
                    <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }}>
                      {r.finShare !== undefined ? (
                        <>
                          {`${(r.finShare * 100).toFixed(0)}%`}
                          <GreyNumeral>{pctNum(r.finShareP)}</GreyNumeral>
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={CELL_SX}>{fmt(r.finPpp, "ppp")}</TableCell>
                    <TableCell sx={CELL_SX}>{fmt(r.passPpp, "ppp")}</TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {anyGrey && (
        <Typography sx={{ fontFamily: CONDENSED_FONT_FAMILY, fontSize: "0.713rem", color: "text.secondary", mt: 0.25 }}>
          Greyed rows: n &lt; {MIN_CHANCES} chances.
        </Typography>
      )}
    </Box>
  );
}

// ---------- defensive overview (impact history) ----------

export function ImpactHistory({ code, scopes }: { code?: string; scopes: PlayerStatsScopeRow[] }) {
  const rows = scopes
    .filter((s) => s.season !== null && val(s, "depm") !== undefined)
    .sort((a, b) => (b.season ?? 0) - (a.season ?? 0))
    .slice(0, 4);
  if (!rows.length) return null;
  const hasWayd = rows.some((r) => val(r, "wdef") !== undefined);
  return (
    <Box>
      <SectionHead code={code} title="Defensive Overview" note="last 4 seasons" />
      <Table size="small" sx={{ width: "100%", ...ZEBRA_SX }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_SX, textAlign: "left" }}>Season</TableCell>
            <TableCell sx={HEAD_SX}>D-EPM</TableCell>
            {hasWayd && <TableCell sx={HEAD_SX}>WAYD Def</TableCell>}
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>On/Off Act</TableCell>
            <TableCell sx={HEAD_SX}>On/Off Exp</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>Off Poss</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key}>
              <TableCell sx={ROW_LABEL_SX}>
                {r.season}-{String(((r.season ?? 0) + 1) % 100).padStart(2, "0")}
              </TableCell>
              <TableCell sx={CELL_SX}>
                <HoverChip value={fmt(val(r, "depm"), "pm")} percentile={pct(r, "depm")} />
              </TableCell>
              {hasWayd && (
                <TableCell sx={CELL_SX}>
                  <HoverChip value={fmt(val(r, "wdef"), "pm")} percentile={pct(r, "wdef")} />
                </TableCell>
              )}
              <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }}>
                <HoverChip value={fmt(val(r, "odef"), "pm")} percentile={pct(r, "odef")} />
              </TableCell>
              <TableCell sx={CELL_SX}>
                <HoverChip value={fmt(val(r, "xdef"), "pm")} percentile={pct(r, "xdef")} />
              </TableCell>
              <TableCell sx={{ ...COUNT_SX, ...GROUP_BORDER }}>{r.poss?.toLocaleString() ?? "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------- closeout breakdown (KPI band + decision table) ----------

function Kpi({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack sx={{ flex: 1, px: 1.25, py: 0.6, borderLeft: "1px solid #eee", "&:first-of-type": { borderLeft: "none" } }}>
      <Box sx={{ fontFamily: DISPLAY_FONT_FAMILY, fontWeight: 800, fontSize: "1.322rem", lineHeight: 1.2 }}>
        {children}
      </Box>
      <Typography
        sx={{
          fontFamily: CONDENSED_FONT_FAMILY,
          fontSize: "0.69rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "text.secondary",
          mt: 0.3,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

const CO_DECISIONS: [string, string][] = [
  ["Shot", "Shot"],
  ["Drive", "Drive"],
  ["Pass", "Pass"],
  ["Other", "Other"],
];

export function CloseoutsSection({ code, scope }: { code?: string; scope: PlayerStatsScopeRow }) {
  if (val(scope, "coPer100") === undefined) return null;
  return (
    <Box>
      <SectionHead code={code} title="Closeout Breakdown" note="attacking closeouts" />
      <Stack
        direction="row"
        sx={{ borderTop: "2px solid #000", borderBottom: "1px solid #e0e0e0", mb: 0.75 }}
      >
        <Kpi label="Closeouts faced /100">
          {fmt(val(scope, "coPer100"), "r1")}
          <GreyNumeral>{pctNum(pct(scope, "coPer100"))}</GreyNumeral>
        </Kpi>
        <Kpi label="Shot rate">
          {fmt(val(scope, "coRateShot"), "pct")}%
          <GreyNumeral>{pctNum(pct(scope, "coRateShot"))}</GreyNumeral>
        </Kpi>
        {/* Pooled percentiles (shot ∪ non-shot on one scale) so the higher PPP
            always reads warmer side by side; falls back to the per-decision
            scale on blobs published before the pooled stat. */}
        <Kpi label="Shot PPP">
          <HoverChip
            value={fmt(val(scope, "coShot"), "ppp")}
            percentile={pct(scope, "coShotPool") ?? pct(scope, "coShot")}
          />
        </Kpi>
        <Kpi label="Non-shot PPP">
          <HoverChip
            value={fmt(val(scope, "coNonshot"), "ppp")}
            percentile={pct(scope, "coNonshotPool") ?? pct(scope, "coNonshot")}
          />
        </Kpi>
      </Stack>
      <Table size="small" sx={{ width: "100%", ...ZEBRA_SX }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_SX, textAlign: "left" }}>Action Taken</TableCell>
            <TableCell sx={HEAD_SX}>Share</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>PPP</TableCell>
            <TableCell sx={HEAD_SX}>xPPP</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={ROW_LABEL_SX}>Overall</TableCell>
            <TableCell sx={CELL_SX} />
            <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }}>
              <HoverChip value={fmt(val(scope, "coPpp"), "ppp")} percentile={pct(scope, "coPpp")} numeral />
            </TableCell>
            <TableCell sx={CELL_SX}>
              <HoverChip value={fmt(val(scope, "coPpp_x"), "ppp")} percentile={pct(scope, "coPpp_x")} />
            </TableCell>
          </TableRow>
          {CO_DECISIONS.map(([label, cap]) => {
            const share = val(scope, `coRate${cap}`);
            if (share === undefined) return null;
            return (
              <TableRow key={cap}>
                <TableCell sx={ROW_LABEL_SX}>{label}</TableCell>
                <TableCell sx={CELL_SX}>
                  {fmt(share, "pct")}%
                  <GreyNumeral>{pctNum(pct(scope, `coRate${cap}`))}</GreyNumeral>
                </TableCell>
                <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }}>
                  <HoverChip value={fmt(val(scope, `co${cap}`), "ppp")} percentile={pct(scope, `co${cap}`)} numeral />
                </TableCell>
                <TableCell sx={CELL_SX}>
                  <HoverChip value={fmt(val(scope, `co${cap}_x`), "ppp")} percentile={pct(scope, `co${cap}_x`)} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------- off-ball screens as the cutter: his shots off them ----------

/** 3P% below this many attempts shows greyed, with no chip (the roll-up ranks it
 * against cutters at or over it, and publishes no percentile below). */
export const OBS_MIN_3PA = 15;

/** The `offense.obs.shots` drill-down: how often a screen that got him the ball
 * became his shot, and what those shots were — the team report's Off-Ball
 * Screens columns, so the two pages read the same. */
export function ObsShotsTable({
  scope,
  section,
}: {
  scope: PlayerStatsScopeRow;
  section: PlayerStatSection;
}) {
  const k = (suffix: string) => `off_obs_sh_${suffix}`;
  const screens = val(scope, k("o_n"));
  const rate = val(scope, k("rate"));
  if (screens === undefined && rate === undefined) return null;
  const fg3a = val(scope, k("3pa"));
  const thin = (fg3a ?? 0) < OBS_MIN_3PA;
  const title = section.nav.slice(1).join(" — ") || section.label;
  return (
    <Box>
      <SectionHead title={title} note="his own shots off his touch-led screens" />
      <Table size="small" sx={{ width: "100%", ...ZEBRA_SX }}>
        <TableHead>
          <TableRow>
            <TableCell sx={HEAD_SX}>Screens</TableCell>
            <TableCell sx={HEAD_SX}>Shot%</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>FGA</TableCell>
            <TableCell sx={HEAD_SX}>3PA Share</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>3PA</TableCell>
            <TableCell sx={HEAD_SX}>3P%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={COUNT_SX}>{fmt(screens, "n0")}</TableCell>
            <TableCell sx={CELL_SX}>
              <HoverChip value={fmt(rate)} percentile={pct(scope, k("rate"))} numeral />
            </TableCell>
            <TableCell sx={{ ...COUNT_SX, ...GROUP_BORDER }}>{fmt(val(scope, k("fga")), "n0")}</TableCell>
            <TableCell sx={CELL_SX}>
              <HoverChip value={fmt(val(scope, k("3pshare")))} percentile={pct(scope, k("3pshare"))} numeral />
            </TableCell>
            <TableCell sx={{ ...COUNT_SX, ...GROUP_BORDER }}>{fmt(fg3a, "n0")}</TableCell>
            <TableCell sx={{ ...CELL_SX, ...(thin ? { opacity: 0.45 } : {}) }}>
              <HoverChip value={fmt(val(scope, k("3p")))} percentile={pct(scope, k("3p"))} numeral plain={thin} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {thin && fg3a !== undefined && (
        <Typography sx={{ fontFamily: CONDENSED_FONT_FAMILY, fontSize: "0.713rem", color: "text.secondary", mt: 0.25 }}>
          Grey 3P%: fewer than {OBS_MIN_3PA} 3PA (no percentile).
        </Typography>
      )}
    </Box>
  );
}

// ---------- generic breakdown table from a served drill-down section ----------

export function BreakdownTable({
  scope,
  section,
}: {
  scope: PlayerStatsScopeRow;
  section: PlayerStatSection;
}) {
  const freqCols = section.columns.filter((c) => c.group.includes("/ 100") && c.key.includes("_f_"));
  const shareCols = section.columns.filter((c) => c.group === "SHARE");
  const pppCols = section.columns.filter(
    (c) => c.group.includes("PPP") && !c.key.endsWith("_x") && !c.key.endsWith("_d") && c.key.includes("_e_"),
  );
  const rows = freqCols
    .map((fc) => {
      const share = shareCols.find((c) => c.label === fc.label);
      const ppp = pppCols.find((c) => c.label === fc.label);
      return {
        label: fc.label,
        per100: val(scope, fc.key),
        share: share ? val(scope, share.key) : undefined,
        shareP: share ? pct(scope, share.key) : undefined,
        ppp: ppp ? val(scope, ppp.key) : undefined,
        pppP: ppp ? pct(scope, ppp.key) : undefined,
        xppp: ppp ? val(scope, ppp.key + "_x") : undefined,
        xpppP: ppp ? pct(scope, ppp.key + "_x") : undefined,
      };
    })
    .filter((r) => r.per100 !== undefined || r.share !== undefined)
    .sort((a, b) => (b.share ?? 0) - (a.share ?? 0));
  if (!rows.length) return null;
  const showX = rows.some((r) => r.xppp !== undefined);
  const title = section.nav.slice(1).join(" — ") || section.label;
  return (
    <Box>
      <SectionHead title={title} />
      <Table size="small" sx={{ width: "100%", ...ZEBRA_SX }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_SX, textAlign: "left" }} />
            <TableCell sx={HEAD_SX}>/100</TableCell>
            <TableCell sx={HEAD_SX}>Share</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>PPP</TableCell>
            {showX && <TableCell sx={HEAD_SX}>xPPP</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.label}>
              <TableCell sx={ROW_LABEL_SX}>{r.label}</TableCell>
              <TableCell sx={CELL_SX}>{fmt(r.per100, "r1")}</TableCell>
              <TableCell sx={CELL_SX}>
                {r.share !== undefined ? `${(r.share * 100).toFixed(0)}%` : "—"}
                <GreyNumeral>{pctNum(r.shareP)}</GreyNumeral>
              </TableCell>
              <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }}>
                <HoverChip value={fmt(r.ppp, "ppp")} percentile={r.pppP} numeral />
              </TableCell>
              {showX && (
                <TableCell sx={CELL_SX}>
                  <HoverChip value={fmt(r.xppp, "ppp")} percentile={r.xpppP} />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------- season history (E4 / two-way) ----------

export function SeasonHistory({
  code,
  scopes,
  leagueTags,
}: {
  code?: string;
  scopes: PlayerStatsScopeRow[];
  leagueTags?: boolean;
}) {
  const rows = scopes
    .filter((s) => s.season !== null)
    .sort((a, b) => (b.season ?? 0) - (a.season ?? 0))
    .slice(0, 6);
  if (!rows.length) return null;
  return (
    <Box>
      <SectionHead code={code} title="Season History" />
      <Table size="small" sx={{ width: "100%", ...ZEBRA_SX }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_SX, textAlign: "left" }}>Season</TableCell>
            {leagueTags && <TableCell sx={HEAD_SX}>League</TableCell>}
            <TableCell sx={HEAD_SX}>GP</TableCell>
            <TableCell sx={HEAD_SX}>Poss</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>USG%</TableCell>
            <TableCell sx={HEAD_SX}>TS%</TableCell>
            <TableCell sx={HEAD_SX}>AST%</TableCell>
            <TableCell sx={HEAD_SX}>TOV%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key}>
              <TableCell sx={ROW_LABEL_SX}>
                {r.season}-{String(((r.season ?? 0) + 1) % 100).padStart(2, "0")}
              </TableCell>
              {leagueTags && <TableCell sx={COUNT_SX}>{r.league ?? "—"}</TableCell>}
              <TableCell sx={COUNT_SX}>{r.gp ?? "—"}</TableCell>
              <TableCell sx={COUNT_SX}>{r.poss?.toLocaleString() ?? "—"}</TableCell>
              <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }}>
                <HoverChip value={fmt(val(r, "usg"), "pct")} percentile={pct(r, "usg")} />
              </TableCell>
              <TableCell sx={CELL_SX}>
                <HoverChip value={fmt(val(r, "ts"), "pct")} percentile={pct(r, "ts")} />
              </TableCell>
              <TableCell sx={CELL_SX}>
                <HoverChip value={fmt(val(r, "ast"), "pct")} percentile={pct(r, "ast")} />
              </TableCell>
              <TableCell sx={CELL_SX}>
                <HoverChip value={fmt(val(r, "tov"), "pct")} percentile={pct(r, "tov")} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------- who he guards / matchup roles (chip-free — personnel pulls #11/#23) ----------

interface OverIndexRow {
  key: string;
  label?: string;
  share: number | null;
  league: number | null;
  x: number | null;
  ppp?: number | null;
  pppPctl?: number | null;
  eppp?: number | null;
  epppPctl?: number | null;
}

function OverIndexTable({
  title,
  note,
  rows,
  footer,
}: {
  title: string;
  note?: string;
  rows: OverIndexRow[];
  footer?: string;
}) {
  if (!rows?.length) return null;
  const shown = rows
    .filter((r) => (r.share ?? 0) >= 0.05 || (r.league ?? 0) >= 0.1)
    .sort((a, b) => (b.x ?? 0) - (a.x ?? 0));
  if (!shown.length) return null;
  // PPP/xPPP allowed (defense-flipped percentiles) — present once regenerated
  // blobs carry matchup efficiency; the columns hide themselves before that.
  const hasEff = shown.some((r) => r.ppp != null);
  return (
    <Box>
      <SectionHead title={title} note={note} />
      <Table size="small" sx={{ width: "100%", ...ZEBRA_SX }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_SX, textAlign: "left" }} />
            <TableCell sx={HEAD_SX}>Him</TableCell>
            <TableCell sx={HEAD_SX}>League</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>×</TableCell>
            {hasEff && (
              <>
                <TableCell sx={{ ...HEAD_SX, ...GROUP_BORDER }}>PPP</TableCell>
                <TableCell sx={HEAD_SX}>xPPP</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((r) => (
            <TableRow key={r.key}>
              <TableCell sx={ROW_LABEL_SX}>{r.label ?? r.key}</TableCell>
              <TableCell sx={CELL_SX}>{r.share !== null ? `${(r.share * 100).toFixed(0)}%` : "—"}</TableCell>
              <TableCell sx={CELL_SX}>{r.league !== null ? `${(r.league * 100).toFixed(0)}%` : "—"}</TableCell>
              <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER, fontWeight: 700 }}>
                {r.x !== null ? `×${r.x.toFixed(1)}` : "—"}
              </TableCell>
              {hasEff && (
                <>
                  <TableCell sx={{ ...CELL_SX, ...GROUP_BORDER }}>
                    <HoverChip value={fmt(r.ppp ?? undefined, "ppp")} percentile={r.pppPctl ?? undefined} />
                  </TableCell>
                  <TableCell sx={CELL_SX}>
                    <HoverChip value={fmt(r.eppp ?? undefined, "ppp")} percentile={r.epppPctl ?? undefined} />
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {footer && (
        <Typography sx={{ fontFamily: CONDENSED_FONT_FAMILY, fontSize: "0.713rem", color: "text.secondary", mt: 0.25 }}>
          {footer}
        </Typography>
      )}
    </Box>
  );
}

export function WhoHeGuards({
  scope,
  seasonLabel,
}: {
  scope: PlayerStatsScopeRow;
  seasonLabel?: string;
}) {
  const m = scope.matchups;
  if (!m?.archetypes?.length) return null;
  // Personnel shows only where he over-indexes — top 3 archetypes by × rate.
  const archetypes = [...m.archetypes]
    .sort((a, b) => (b.x ?? 0) - (a.x ?? 0))
    .slice(0, 3);
  const top = (m.top ?? [])
    .slice(0, 4)
    .map((t) => `${t.name} (${Math.round(t.poss)})`)
    .join(" · ");
  // A season past WAYD's last reads that season's archetypes (the roll-up's stand-in, Trevor 2026-10-20)
  const stand = m.archetypeSeason
    ? `Archetypes from ${m.archetypeSeason}-${String((m.archetypeSeason + 1) % 100).padStart(2, "0")}`
    : null;
  const footer = [
    top ? `Top matchups: ${top}.` : null,
    stand,
    "× = his share vs the league-average matchup mix",
    "PPP/xPPP = allowed per chance guarding them",
    m.matchupPoss ? `${Math.round(m.matchupPoss).toLocaleString()} matchup poss` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <OverIndexTable
      title={`Who He Guards${seasonLabel ? ` — ${seasonLabel}` : ""}`}
      note="top 3 by ×, weighted by archetype probabilities"
      rows={archetypes}
      footer={footer}
    />
  );
}

export function MatchupRoles({
  scope,
  seasonLabel,
}: {
  scope: PlayerStatsScopeRow;
  seasonLabel?: string;
}) {
  const m = scope.matchups;
  if (!m?.roles?.length) return null;
  return (
    <OverIndexTable
      title={`Matchups by Lineup Role${seasonLabel ? ` — ${seasonLabel}` : ""}`}
      rows={m.roles}
      footer="Each opposing lineup classified by season rates. PPP/xPPP = allowed per chance guarding that role."
    />
  );
}
